import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { useEffect, useRef, useState } from "react";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import ActiveJobsDrawer from "./ActiveJobsDrawer/ActiveJobsDrawer";
import ArchiveGrid from "./ArchiveGrid/ArchiveGrid";
import ArchiveSideNav from "./ArchiveSideNav/ArchiveSideNav";
import ArchiveToolbar from "./ArchiveToolbar/ArchiveToolbar";
import {
	archiveDownloadEndpoint,
	archiveJobsEndpoint,
	archiveTreeEndpoint,
	downloadByUrl,
	itemTypes,
	jobsPollingTime,
	normalizeArchiveNode,
	rootArchivePath,
} from "./data";

const ShootArchive = () => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [currentPath, setCurrentPath] = useState("");
	const [currentItems, setCurrentItems] = useState([]);
	const [selectedNodePath, setSelectedNodePath] = useState("");
	const [jobsDrawerOpen, setJobsDrawerOpen] = useState(false);
	const [activeJobs, setActiveJobs] = useState([]);
	const [loadingTree, setLoadingTree] = useState(false);
	const [loadingJobs, setLoadingJobs] = useState(false);
	const downloadAnchorRef = useRef(null);

	const fetchArchiveTree = async (path = "") => {
		setLoadingTree(true);
		try {
			const res = await axios.get(archiveTreeEndpoint, { params: path ? { path } : {} });
			const rawNodes = Array.isArray(res.data?.result) ? res.data.result : [];
			setCurrentItems(rawNodes.map((node) => normalizeArchiveNode(node)));
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
			setCurrentItems([]);
		} finally {
			setLoadingTree(false);
		}
	};

	const fetchActiveJobs = async () => {
		setLoadingJobs(true);
		try {
			const res = await axios.get(archiveJobsEndpoint);
			setActiveJobs(Array.isArray(res.data?.result) ? res.data.result : []);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
		} finally {
			setLoadingJobs(false);
		}
	};

	useEffect(() => {
		fetchArchiveTree(currentPath);
	}, [currentPath]);

	useEffect(() => {
		if (!jobsDrawerOpen) return undefined;
		fetchActiveJobs();
		const intervalId = setInterval(fetchActiveJobs, jobsPollingTime);
		return () => clearInterval(intervalId);
	}, [jobsDrawerOpen]);

	const handleOpenNode = (node) => {
		setSelectedNodePath(node.path);
		if (node.type === itemTypes.folder) {
			setCurrentPath(node.path);
			return;
		}
		if (node.url) window.open(node.url, "_blank", "noopener,noreferrer");
	};

	const handleArchiveDownload = async (node) => {
		if (node.type !== itemTypes.folder && node.url) {
			setLoadingOpen(true);
			try {
				await downloadByUrl(node.url, node.name, downloadAnchorRef);
			} catch (error) {
				window.open(node.url, "_blank", "noopener,noreferrer");
			} finally {
				setLoadingOpen(false);
			}
			return;
		}

		setLoadingOpen(true);
		try {
			const relativePath = node.path.replace(`${rootArchivePath}/`, "").replace(`${rootArchivePath}`, "");
			const folderName = relativePath.split("/").filter(Boolean).join("_") || node.name;
			await axios.post(archiveDownloadEndpoint, { type: node.type, path: node.path, name: `${folderName}` });
			setJobsDrawerOpen(true);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Download job created. Tracking active jobs.", severity: "success" });
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	const breadcrumbParts = currentPath ? currentPath.replace(`${rootArchivePath}/`, "").split("/").filter(Boolean) : [];
	const currentFolderName = breadcrumbParts[breadcrumbParts.length - 1] || "Root";

	return (
		<Box width="100%" height="calc(100vh - 64px - 72px)" display="flex" flexDirection="column">
			<a ref={downloadAnchorRef} style={{ display: "none" }} />

			<Box
				sx={{
					borderRadius: 2,
					overflow: "hidden",
					flex: 1,
					display: "flex",
					flexDirection: "column",
					border: `1px solid ${grey[200]}`,
					bgcolor: "background.paper",
				}}>
				<Box display="flex" flex={1} overflow="hidden" flexWrap={{ xs: "wrap", lg: "nowrap" }}>
					<ArchiveSideNav
						currentPath={currentPath}
						currentFolderName={currentFolderName}
						breadcrumbParts={breadcrumbParts}
						onNavigate={(path, selectedPath) => {
							setCurrentPath(path);
							setSelectedNodePath(selectedPath || "");
						}}
					/>

					<Box flex={1} minWidth={0}>
						<ArchiveToolbar
							currentPath={currentPath}
							currentFolderName={currentFolderName}
							breadcrumbParts={breadcrumbParts}
							onNavigate={setCurrentPath}
							onDownload={handleArchiveDownload}
							onRefresh={() => fetchArchiveTree(currentPath)}
							onOpenJobs={() => setJobsDrawerOpen(true)}
						/>
						<ArchiveGrid
							items={currentItems}
							loading={loadingTree}
							selectedNodePath={selectedNodePath}
							onOpen={handleOpenNode}
							onDownload={handleArchiveDownload}
						/>
					</Box>
				</Box>
			</Box>

			<ActiveJobsDrawer
				open={jobsDrawerOpen}
				onClose={() => setJobsDrawerOpen(false)}
				activeJobs={activeJobs}
				loading={loadingJobs}
				onRefresh={fetchActiveJobs}
				downloadAnchorRef={downloadAnchorRef}
			/>
		</Box>
	);
};

export default ShootArchive;
